import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { ApolloProvider } from 'react-apollo';
import { useIntl } from 'react-intl';
import ChatWidget from '../../../../../containers/ChatWidgetContainer';
import { useSuccessionTheme } from '../../../SuccessionThemeProvider';

// Simple start chat component to replace the deleted Material-UI version
const StartChatWidget = ({ userName, createChat, startConvo }) => {
  const intl = useIntl();
  const handleStartChat = () => {
    if (createChat) {
      createChat(userName);
    }
  };

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6 text-center transition-colors duration-300">
      <div className="mb-4">
        <svg className="w-12 h-12 mx-auto text-succession-primary transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </div>
      <h4 className="text-lg font-semibold text-succession-text-primary mb-2 transition-colors duration-300">
        {intl.formatMessage({ id: 'succession.profile_drawer.messages_tab.start_conversation' })}
      </h4>
      <p className="text-sm text-succession-text-muted mb-4 transition-colors duration-300">
        {intl.formatMessage({ id: 'succession.profile_drawer.messages_tab.send_message_prompt' })}
      </p>
      <button
        onClick={handleStartChat}
        className="px-6 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
      >
        {intl.formatMessage({ id: 'succession.profile_drawer.messages_tab.send_message' })}
      </button>
    </div>
  );
};

StartChatWidget.propTypes = {
  userName: PropTypes.string.isRequired,
  createChat: PropTypes.func.isRequired,
  startConvo: PropTypes.bool,
};

const MessagesTab = ({
  showChatBox,
  chatId,
  startConvo,
  createChat,
  user,
  targetUser,
  currentUser,
  apolloClient
}) => {
  const intl = useIntl();
  // Use succession theme context
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();

  // Use either user or targetUser, ensuring backwards compatibility
  const profileUser = targetUser || user;

  // Safety check for Apollo client
  if (!apolloClient) {
    return (
      <div className="p-4 bg-succession-bg-primary rounded-lg shadow-succession-md border border-succession-border transition-colors duration-300">
        <p className="text-succession-error font-medium transition-colors duration-300">
          {intl.formatMessage({ id: 'succession.profile_drawer.messages_tab.chat_unavailable' })}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full">
      <h3 className="text-sm font-semibold text-succession-text-primary mb-4 transition-colors duration-300">
        {intl.formatMessage({ id: 'succession.profile_drawer.messages_tab.messages_heading' })}
      </h3>
      <ApolloProvider client={apolloClient}>
        {showChatBox ? (
          <ChatWidget 
            room_id={chatId} 
            currentUser={currentUser} 
            title={`${get(profileUser, 'user_name')}`} 
          />
        ) : (
          <StartChatWidget
            userName={`${get(profileUser, 'user_name')}`}
            createChat={createChat}
            startConvo={startConvo}
          />
        )}
      </ApolloProvider>
    </div>
  );
};

MessagesTab.propTypes = {
  showChatBox: PropTypes.bool.isRequired,
  chatId: PropTypes.string.isRequired,
  startConvo: PropTypes.bool.isRequired,
  createChat: PropTypes.func.isRequired,
  user: PropTypes.object,
  targetUser: PropTypes.object,
  currentUser: PropTypes.object.isRequired,
  apolloClient: PropTypes.object.isRequired,
};

// Default props to handle either user or targetUser being provided
MessagesTab.defaultProps = {
  user: null,
  targetUser: null,
};

export default MessagesTab; 